import { useEffect, useState, useCallback } from 'react'
import { Link } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import { Student, Tutor } from '../types'
import { useAuth } from '../contexts/AuthContext'
import { Plus, Edit, Trash2, Search, User, FileDown, Filter, ChevronDown, ChevronRight, DollarSign, CalendarPlus } from 'lucide-react'
import { ViewToggle, useStoredView } from '../components/ViewToggle'
import { format } from 'date-fns'
import { sessionsLabel } from '../lib/balance'
import { fetchMyTutorIds } from '../lib/roles'
import { ActivityClock } from '../components/ActivityClock'
import { hasAdminRights } from '../lib/roles'

const inputCls =
  'w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-400 bg-white dark:bg-surface-0 text-gray-900 dark:text-white'
const labelCls = 'block text-xs text-gray-500 dark:text-gray-400 mb-1'

// A student's balances: the total is pre (prepaid) + post (Bill later minutes still to bill,
// 0 or below). Under the total: the split for Bill later students and anyone still owing post
// minutes, otherwise the sessions left.
const balanceParts = (s: Student) => {
  const pre = s.balanceMinutes
  const post = s.postBalanceMinutes ?? 0
  const split = !!s.billLater || post < 0
  return {
    pre,
    post,
    total: pre + post,
    noMinutes: !split && pre <= 0,
    detail: split ? `pre ${pre} · post ${post}` : `${sessionsLabel(pre, s.sessionLengthMinutes ?? 60)} left`,
  }
}

export function Students() {
  const { profile } = useAuth()
  const [students, setStudents] = useState<Student[]>([])
  const [tutors, setTutors] = useState<Tutor[]>([])
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [showAddModal, setShowAddModal] = useState(false)
  const [adding, setAdding] = useState(false)
  const [addError, setAddError] = useState<string | null>(null)
  const [showColumnFilter, setShowColumnFilter] = useState(false)
  const [view, changeView] = useStoredView('studentsView')
  const [visibleColumns, setVisibleColumns] = useState({
    name: true,
    grade: true,
    tutor: true,
    balance: true,
    guardian: true,
    phone: true,
    status: true,
    actions: true
  })
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    address: '',
    grade: '',
    tutorId: '',
    guardianName: '',
    guardianEmail: '',
    guardianPhone: '',
    guardianAddress: '',
    pricePerSession: '',
    insuranceCoveragePercent: '',
    sessionLengthMinutes: '60',
    notes: ''
  })

  const fetchStudents = useCallback(async () => {
    try {
      if (profile?.role === 'tutor') {
        const tutorIds = await fetchMyTutorIds()

        if (tutorIds.length === 0) {
          console.log('No tutor record found for this user')
          setStudents([])
          setLoading(false)
          return
        }
      }

      const { data, error } = await supabase
        .from('students')
        .select('*')
        .order('created_at', { ascending: false })

      if (error) throw error

      const formattedStudents = data?.map(student => ({
        id: student.id,
        name: student.name,
        phone: student.phone,
        address: student.address || null,
        grade: student.grade,
        tutorId: student.tutor_id,
        guardianName: student.guardian_name,
        guardianEmail: student.guardian_email,
        guardianPhone: student.guardian_phone,
        guardianAddress: student.guardian_address,
        balanceMinutes: student.balance_minutes,
        billLater: !!student.bill_later,
        postBalanceMinutes: student.post_balance_minutes ?? 0,
        sessionLengthMinutes: student.session_length_minutes,
        status: student.status,
        notes: student.notes,
        createdAt: student.created_at
      })) || []

      setStudents(formattedStudents)
    } catch (error) {
      console.error('Error fetching students:', error)
    } finally {
      setLoading(false)
    }
  }, [profile])

  const fetchTutors = useCallback(async () => {
    try {
      const { data, error } = await supabase
        .from('tutors')
        .select('id, name')
        .order('name')

      if (error) throw error

      setTutors(data?.map(tutor => ({
        id: tutor.id,
        name: tutor.name,
        email: '',
        subjects: [],
        hourlyRate: 0,
        totalMinutesAvailable: 0,
        totalMinutesUsed: 0
      })) || [])
    } catch (error) {
      console.error('Error fetching tutors:', error)
    }
  }, [])

  useEffect(() => {
    if (profile) {
      fetchStudents()
      fetchTutors()
    }
  }, [profile, fetchStudents, fetchTutors])

  const handleAddStudent = async () => {
    // Only allow admins to add students
    if (!profile || !hasAdminRights(profile.role)) {
      console.error('Unauthorized: Only admins can add students')
      return
    }

    const name = formData.name.trim()
    if (!name) {
      setAddError("Enter the student's name.")
      return
    }

    setAdding(true)
    setAddError(null)
    try {
      const { data: newStudent, error } = await supabase.from('students').insert({
        name,
        phone: formData.phone,
        address: formData.address,
        grade: formData.grade,
        tutor_id: formData.tutorId || null,
        guardian_name: formData.guardianName,
        guardian_email: formData.guardianEmail,
        guardian_phone: formData.guardianPhone,
        guardian_address: formData.guardianAddress,
        session_length_minutes: formData.sessionLengthMinutes === '' ? 60 : parseInt(formData.sessionLengthMinutes),
        notes: formData.notes
      }).select('id').single()

      if (error) throw error

      // Price + insurance are money — stored in the admin-only student_billing table.
      const price = formData.pricePerSession === '' ? null : parseFloat(formData.pricePerSession)
      const insurance = formData.insuranceCoveragePercent === '' ? null : parseFloat(formData.insuranceCoveragePercent)
      if (newStudent && (price !== null || insurance !== null)) {
        await supabase.from('student_billing').insert({
          student_id: newStudent.id,
          price_per_session: price,
          insurance_coverage_percent: insurance,
        })
      }

      setShowAddModal(false)
      fetchStudents()
      setFormData({
        name: '',
        phone: '',
        address: '',
        grade: '',
        tutorId: '',
        guardianName: '',
        guardianEmail: '',
        guardianPhone: '',
        guardianAddress: '',
        pricePerSession: '',
        insuranceCoveragePercent: '',
        sessionLengthMinutes: '60',
        notes: ''
      })
    } catch (error: any) {
      console.error('Error adding student:', error)
      setAddError(error?.message ? `Couldn't add the student: ${error.message}` : "Couldn't add the student. Please try again.")
    } finally {
      setAdding(false)
    }
  }

  const handleDeleteStudent = async (id: string) => {
    // Only allow admins to delete students
    if (!profile || !hasAdminRights(profile.role)) {
      console.error('Unauthorized: Only admins can delete students')
      return
    }

    if (!window.confirm('Are you sure you want to delete this student?')) return

    try {
      const { error } = await supabase.from('students').delete().eq('id', id)
      if (error) throw error
      fetchStudents()
    } catch (error) {
      console.error('Error deleting student:', error)
    }
  }

  const exportToCSV = () => {
    const csvData = filteredStudents.map(student => {
      const row: any = {}
      if (visibleColumns.name) {
        row.Name = student.name
      }
      if (visibleColumns.grade) {
        row.Grade = student.grade || 'N/A'
      }
      if (visibleColumns.tutor) {
        row.Tutor = getTutorName(student.tutorId)
      }
      if (visibleColumns.balance) {
        row['Pre Balance (Minutes)'] = student.balanceMinutes
        row['Post Balance (Minutes)'] = balanceParts(student).post
        row['Total Balance (Minutes)'] = balanceParts(student).total
        row['Bill Later'] = student.billLater ? 'yes' : 'no'
      }
      if (visibleColumns.guardian) {
        row['Guardian Name'] = student.guardianName || 'N/A'
        row['Guardian Email'] = student.guardianEmail || 'N/A'
      }
      if (visibleColumns.phone) {
        row['Student Phone'] = student.phone || 'N/A'
        row['Guardian Phone'] = student.guardianPhone || 'N/A'
      }
      if (visibleColumns.status) {
        row.Status = student.status || 'active'
      }
      return row
    })

    if (csvData.length === 0) {
      alert('No data to export')
      return
    }

    const headers = Object.keys(csvData[0] || {})
    const csvContent = [
      headers.join(','),
      ...csvData.map(row =>
        headers.map(header =>
          `"${String(row[header as keyof typeof row] || '').replace(/"/g, '""')}"`
        ).join(',')
      )
    ].join('\n')

    const blob = new Blob([csvContent], { type: 'text/csv' })
    const url = window.URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `students_${format(new Date(), 'yyyy-MM-dd')}.csv`
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    window.URL.revokeObjectURL(url)
  }

  const filteredStudents = students.filter(student =>
    student.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    student.guardianEmail?.toLowerCase().includes(searchTerm.toLowerCase())
  )

  const getTutorName = (tutorId: string) => {
    const tutor = tutors.find(t => t.id === tutorId)
    return tutor?.name || 'Unassigned'
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-400"></div>
      </div>
    )
  }

  return (
    // List view uses the full width so the table's columns fit.
    <div className={view === 'list' ? '' : 'max-w-7xl mx-auto'}>
      {/* Header */}
      <div className="mb-6 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            {profile?.role === 'tutor' ? 'My Students' : 'Students'}
          </h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            {filteredStudents.length} {filteredStudents.length === 1 ? 'student' : 'students'} found
          </p>
        </div>
        <div className="flex gap-2">
          <ActivityClock scope="students" />
          <ViewToggle view={view} onChange={changeView} />
          <button
            onClick={exportToCSV}
            className="flex items-center px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
          >
            <FileDown className="h-5 w-5 mr-2" />
            Export CSV
          </button>
          <div className="relative">
            <button
              onClick={() => setShowColumnFilter(!showColumnFilter)}
              className="flex items-center px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
            >
              <Filter className="h-5 w-5 mr-2" />
              Columns
              <ChevronDown className="h-4 w-4 ml-1" />
            </button>
            {showColumnFilter && (
              <div className="absolute right-0 mt-2 w-48 bg-white dark:bg-surface-100 rounded-lg shadow-lg z-10 p-3">
                <div className="space-y-2">
                  {Object.entries(visibleColumns).map(([key, value]) => (
                    <label key={key} className="flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={value}
                        onChange={(e) => setVisibleColumns({ ...visibleColumns, [key]: e.target.checked })}
                        className="w-4 h-4 text-brand-600 rounded focus:ring-brand-500"
                      />
                      <span className="ml-2 text-sm text-gray-700 dark:text-gray-300 capitalize">
                        {key === 'balance' ? 'Balance' : key === 'actions' ? 'Actions' : key}
                      </span>
                    </label>
                  ))}
                </div>
              </div>
            )}
          </div>
          {hasAdminRights(profile?.role) && (
            <button
              onClick={() => setShowAddModal(true)}
              className="flex items-center px-4 py-2 bg-brand-600 text-white rounded-lg hover:bg-brand-700 transition-colors shadow-sm"
            >
              <Plus className="h-5 w-5 mr-2" />
              Add Student
            </button>
          )}
        </div>
      </div>

      {/* Search Bar */}
      <div className="mb-6 relative">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
        <input
          type="text"
          placeholder="Search by name, phone, or guardian..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full pl-10 pr-4 py-2.5 bg-white dark:bg-surface-100 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-400 text-gray-900 dark:text-white"
        />
      </div>

      {/* Students */}
      {view === 'grid' ? (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredStudents.map((student) => (
          <div key={student.id} className="bg-white dark:bg-surface-100 rounded-lg shadow p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center">
                <div className="bg-brand-100 dark:bg-brand-800 p-3 rounded-full">
                  <User className="h-6 w-6 text-brand-600 dark:text-brand-400" />
                </div>
                <div className="ml-4">
                  {visibleColumns.name && (
                    <Link
                      to={`/dashboard/students/${student.id}`}
                      className="group inline-flex items-center gap-1 text-lg font-semibold text-gray-900 dark:text-white hover:text-brand-600 dark:hover:text-brand-400 transition-colors"
                    >
                      {student.name}
                      <ChevronRight className="h-4 w-4 text-gray-300 group-hover:text-brand-500 group-hover:translate-x-0.5 transition-all" />
                    </Link>
                  )}
                  {visibleColumns.grade && (
                    <p className="text-sm text-gray-500 dark:text-gray-400">Grade: {student.grade || 'N/A'}</p>
                  )}
                </div>
              </div>
              {visibleColumns.actions && (
                <div className="flex space-x-2">
                  <Link
                    to={`/dashboard/students/${student.id}`}
                    title="View profile"
                    className="text-brand-600 hover:text-brand-900 dark:text-brand-400 dark:hover:text-brand-300"
                  >
                    <Edit className="h-5 w-5" />
                  </Link>
                  {hasAdminRights(profile?.role) && (
                    <button
                      onClick={() => handleDeleteStudent(student.id)}
                      className="text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300"
                    >
                      <Trash2 className="h-5 w-5" />
                    </button>
                  )}
                </div>
              )}
            </div>
            <div className="space-y-2">
              {visibleColumns.tutor && (
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  <span className="font-medium">Tutor:</span> {getTutorName(student.tutorId)}
                </p>
              )}
              {visibleColumns.balance && (() => {
                const b = balanceParts(student)
                return (
                  <p className={`text-sm ${b.total <= 0 ? 'text-red-600 dark:text-red-400' : 'text-gray-600 dark:text-gray-400'}`}>
                    <span className="font-medium">Total balance:</span> {b.total} minutes · {b.detail}
                    {student.billLater ? (
                      <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-brand-400/15 text-brand-600 dark:text-brand-400">
                        Bill later
                      </span>
                    ) : b.pre <= 0 && (
                      <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300">
                        No minutes: can't log sessions
                      </span>
                    )}
                  </p>
                )
              })()}
              {visibleColumns.guardian && (
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  <span className="font-medium">Guardian:</span> {student.guardianName || 'N/A'}
                </p>
              )}
              {visibleColumns.phone && (
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  <span className="font-medium">Phone:</span> {student.phone || 'N/A'}
                </p>
              )}
              {visibleColumns.status && (
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  <span className="font-medium">Status:</span>
                  <span className={`ml-2 px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                    (student.status || 'active') === 'active'
                      ? 'bg-green-100 text-green-800 dark:bg-green-800 dark:text-green-100'
                      : 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-100'
                  }`}>
                    {student.status || 'active'}
                  </span>
                </p>
              )}
            </div>
            {student.createdAt && (
              <div className="mt-4 pt-3 border-t border-gray-100 dark:border-gray-700/60 flex items-center gap-1.5 text-xs text-gray-400 dark:text-gray-500">
                <CalendarPlus className="h-3.5 w-3.5" />
                Added {format(new Date(student.createdAt), 'MMM d, yyyy')}
              </div>
            )}
          </div>
        ))}
      </div>
      ) : filteredStudents.length > 0 && (
      /* List view */
      <div className="bg-white dark:bg-surface-100 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-surface-0">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Student</th>
                {visibleColumns.tutor && <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Tutor</th>}
                {visibleColumns.balance && <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Total balance</th>}
                {visibleColumns.guardian && <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Guardian</th>}
                {visibleColumns.phone && <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Phone</th>}
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Added</th>
                {visibleColumns.actions && <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Actions</th>}
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-surface-100 divide-y divide-gray-200 dark:divide-gray-700">
              {filteredStudents.map((student) => (
                <tr key={student.id} className="group hover:bg-gray-50 dark:hover:bg-surface-0/60 transition-colors">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      <div className="flex-shrink-0 bg-brand-100 dark:bg-brand-800 p-2 rounded-full">
                        <User className="h-5 w-5 text-brand-600 dark:text-brand-400" />
                      </div>
                      <div className="min-w-0">
                        <Link
                          to={`/dashboard/students/${student.id}`}
                          className="group/link inline-flex items-center gap-1 text-sm font-medium text-gray-900 dark:text-white hover:text-brand-600 dark:hover:text-brand-400 transition-colors"
                        >
                          {student.name}
                          <ChevronRight className="h-3.5 w-3.5 text-gray-300 group-hover/link:text-brand-500 group-hover/link:translate-x-0.5 transition-all" />
                        </Link>
                        {(visibleColumns.status || (visibleColumns.grade && student.grade)) && (
                          <span className="flex items-center gap-1.5">
                            {visibleColumns.status && (
                              <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[11px] font-medium capitalize ${
                                (student.status || 'active') === 'active'
                                  ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
                                  : 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-100'
                              }`}>
                                {student.status || 'active'}
                              </span>
                            )}
                            {visibleColumns.grade && student.grade && (
                              <span className="text-xs text-gray-500 dark:text-gray-400">Grade: {student.grade}</span>
                            )}
                          </span>
                        )}
                      </div>
                    </div>
                  </td>
                  {visibleColumns.tutor && <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300">{getTutorName(student.tutorId)}</td>}
                  {visibleColumns.balance && (() => {
                    const b = balanceParts(student)
                    return (
                      <td className="px-4 py-3 whitespace-nowrap text-sm">
                        <span className={`font-medium ${b.total <= 0 ? 'text-red-600 dark:text-red-400' : 'text-gray-900 dark:text-white'}`}>
                          {b.total} min
                        </span>
                        <span className="block text-xs text-gray-500 dark:text-gray-400">
                          {b.noMinutes ? "Can't log sessions" : `${b.detail}${student.billLater ? ' · bill later' : ''}`}
                        </span>
                      </td>
                    )
                  })()}
                  {visibleColumns.guardian && <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300">{student.guardianName || '—'}</td>}
                  {visibleColumns.phone && <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600 dark:text-gray-300">{student.phone || '—'}</td>}
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                    {student.createdAt ? format(new Date(student.createdAt), 'MMM d, yyyy') : '—'}
                  </td>
                  {visibleColumns.actions && (
                    <td className="px-4 py-3 whitespace-nowrap text-right text-sm">
                      <div className="flex items-center justify-end gap-1 opacity-60 group-hover:opacity-100 transition-opacity">
                        <Link to={`/dashboard/students/${student.id}`} title="View profile" className="p-2 rounded-lg text-gray-500 hover:text-brand-500 hover:bg-brand-500/10 dark:text-gray-400 transition-colors">
                          <Edit className="h-4 w-4" />
                        </Link>
                        {hasAdminRights(profile?.role) && (
                          <button onClick={() => handleDeleteStudent(student.id)} title="Delete student" className="p-2 rounded-lg text-gray-500 hover:text-red-500 hover:bg-red-500/10 dark:text-gray-400 transition-colors">
                            <Trash2 className="h-4 w-4" />
                          </button>
                        )}
                      </div>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      )}
      {filteredStudents.length === 0 && (
        <div className="text-center py-8 bg-white dark:bg-surface-100 rounded-lg">
          <p className="text-gray-500 dark:text-gray-400">No students found</p>
        </div>
      )}

      {/* Add Student Modal - Only for admins */}
      {showAddModal && hasAdminRights(profile?.role) && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-surface-100 rounded-lg p-6 w-full max-w-md max-h-[90vh] overflow-y-auto">
            <h2 className="text-xl font-bold mb-4 text-gray-900 dark:text-white">Add New Student</h2>
            <div className="space-y-4">
              <label className="block">
                <span className={labelCls}>Student name <span className="text-red-500">*</span></span>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  autoFocus
                  className={inputCls}
                />
              </label>
              <label className="block">
                <span className={labelCls}>Phone</span>
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  className={inputCls}
                />
              </label>
              <label className="block">
                <span className={labelCls}>Student address</span>
                <input
                  type="text"
                  value={formData.address}
                  onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                  className={inputCls}
                />
              </label>
              <label className="block">
                <span className={labelCls}>Grade</span>
                <input
                  type="text"
                  value={formData.grade}
                  onChange={(e) => setFormData({ ...formData, grade: e.target.value })}
                  className={inputCls}
                />
              </label>
              <label className="block">
                <span className={labelCls}>Tutor</span>
                <select
                  value={formData.tutorId}
                  onChange={(e) => setFormData({ ...formData, tutorId: e.target.value })}
                  className={inputCls}
                >
                  <option value="">No tutor yet</option>
                  {tutors.map(tutor => (
                    <option key={tutor.id} value={tutor.id}>{tutor.name}</option>
                  ))}
                </select>
              </label>
              <label className="block">
                <span className={labelCls}>Guardian name</span>
                <input
                  type="text"
                  value={formData.guardianName}
                  onChange={(e) => setFormData({ ...formData, guardianName: e.target.value })}
                  className={inputCls}
                />
              </label>
              <label className="block">
                <span className={labelCls}>Guardian email</span>
                <input
                  type="email"
                  value={formData.guardianEmail}
                  onChange={(e) => setFormData({ ...formData, guardianEmail: e.target.value })}
                  className={inputCls}
                />
              </label>
              <label className="block">
                <span className={labelCls}>Guardian phone</span>
                <input
                  type="tel"
                  value={formData.guardianPhone}
                  onChange={(e) => setFormData({ ...formData, guardianPhone: e.target.value })}
                  className={inputCls}
                />
              </label>
              <label className="block">
                <span className={labelCls}>Guardian address</span>
                <input
                  type="text"
                  value={formData.guardianAddress}
                  onChange={(e) => setFormData({ ...formData, guardianAddress: e.target.value })}
                  className={inputCls}
                />
              </label>

              {/* Billing & sessions */}
              <div className="pt-1">
                <p className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-gray-400 dark:text-gray-500 mb-2">
                  <DollarSign className="h-3.5 w-3.5" /> Billing &amp; sessions
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <label className="block">
                    <span className="block text-xs text-gray-500 dark:text-gray-400 mb-1">Price / session ($)</span>
                    <input
                      type="number" min="0" step="0.01" placeholder="e.g. 60"
                      value={formData.pricePerSession}
                      onChange={(e) => setFormData({ ...formData, pricePerSession: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-400 bg-white dark:bg-surface-0 text-gray-900 dark:text-white"
                    />
                  </label>
                  <label className="block">
                    <span className="block text-xs text-gray-500 dark:text-gray-400 mb-1">Insurance (%)</span>
                    <input
                      type="number" min="0" max="100" step="1" placeholder="e.g. 50"
                      value={formData.insuranceCoveragePercent}
                      onChange={(e) => setFormData({ ...formData, insuranceCoveragePercent: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-400 bg-white dark:bg-surface-0 text-gray-900 dark:text-white"
                    />
                  </label>
                  <label className="block">
                    <span className="block text-xs text-gray-500 dark:text-gray-400 mb-1">Session (min)</span>
                    <input
                      type="number" min="1" step="1" placeholder="60"
                      value={formData.sessionLengthMinutes}
                      onChange={(e) => setFormData({ ...formData, sessionLengthMinutes: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-400 bg-white dark:bg-surface-0 text-gray-900 dark:text-white"
                    />
                  </label>
                </div>
                <p className="mt-1.5 text-[11px] text-gray-400">Optional — used to auto-fill minutes on top-ups. Leave price/insurance blank if not set yet.</p>
              </div>

              <label className="block">
                <span className={labelCls}>Notes</span>
                <textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  className={inputCls}
                  rows={3}
                />
              </label>
            </div>
            {addError && (
              <p className="mt-4 text-sm text-red-600 dark:text-red-400">{addError}</p>
            )}
            <div className="mt-6 flex justify-end space-x-3">
              <button
                onClick={() => { setShowAddModal(false); setAddError(null) }}
                className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-100 dark:hover:bg-surface-0 text-gray-700 dark:text-gray-300"
              >
                Cancel
              </button>
              <button
                onClick={handleAddStudent}
                disabled={adding || !formData.name.trim()}
                className="px-4 py-2 bg-brand-600 text-white rounded-lg hover:bg-brand-800 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {adding ? 'Adding…' : 'Add Student'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}